/**
 * @fileoverview Plan Enforcement Error Codes
 * 
 * This file defines the standardized error codes for plan-based enforcement.
 * These codes are used across the application to provide consistent error
 * responses when plan limits are exceeded or capabilities are not available.
 */

/**
 * Plan enforcement error codes
 */
export enum PlanErrorCode {
  /** Export format not allowed for current plan */
  PLAN_FORBIDDEN_FORMAT = 'PLAN_FORBIDDEN_FORMAT',
  
  /** Sharing mode not allowed for current plan */
  PLAN_FORBIDDEN_SHARING_MODE = 'PLAN_FORBIDDEN_SHARING_MODE',
  
  /** Shape limit exceeded for current plan */
  PLAN_SHAPE_LIMIT_EXCEEDED = 'PLAN_SHAPE_LIMIT_EXCEEDED',
  
  /** Plan upgrade required for this action */
  PLAN_UPGRADE_REQUIRED = 'PLAN_UPGRADE_REQUIRED',
  
  /** Entitlements not resolved for this request */
  ENTITLEMENTS_NOT_RESOLVED = 'ENTITLEMENTS_NOT_RESOLVED',
  
  /** Entitlements resolution failed */
  ENTITLEMENTS_ERROR = 'ENTITLEMENTS_ERROR',
  
  /** Missing capability parameter */
  MISSING_CAPABILITY = 'MISSING_CAPABILITY',
  
  /** Missing format parameter */
  MISSING_FORMAT = 'MISSING_FORMAT',
  
  /** Capability check failed */
  CAPABILITY_CHECK_ERROR = 'CAPABILITY_CHECK_ERROR',
  
  /** Export format check failed */
  EXPORT_FORMAT_CHECK_ERROR = 'EXPORT_FORMAT_CHECK_ERROR',
  
  /** Shape limit check failed */
  SHAPE_LIMIT_ERROR = 'SHAPE_LIMIT_ERROR'
}

/**
 * Error code descriptions for client display
 */
export const PLAN_ERROR_DESCRIPTIONS: Record<PlanErrorCode, string> = {
  [PlanErrorCode.PLAN_FORBIDDEN_FORMAT]: 'This export format is not available in your current plan',
  [PlanErrorCode.PLAN_FORBIDDEN_SHARING_MODE]: 'This sharing mode is not available in your current plan',
  [PlanErrorCode.PLAN_SHAPE_LIMIT_EXCEEDED]: 'You have reached the shape limit for your current plan',
  [PlanErrorCode.PLAN_UPGRADE_REQUIRED]: 'This feature requires a plan upgrade',
  [PlanErrorCode.ENTITLEMENTS_NOT_RESOLVED]: 'Unable to determine your plan capabilities',
  [PlanErrorCode.ENTITLEMENTS_ERROR]: 'Failed to retrieve plan information',
  [PlanErrorCode.MISSING_CAPABILITY]: 'Capability parameter is required',
  [PlanErrorCode.MISSING_FORMAT]: 'Format parameter is required',
  [PlanErrorCode.CAPABILITY_CHECK_ERROR]: 'Failed to check capability',
  [PlanErrorCode.EXPORT_FORMAT_CHECK_ERROR]: 'Failed to check export format',
  [PlanErrorCode.SHAPE_LIMIT_ERROR]: 'Failed to get shape limit'
};

/**
 * Error code HTTP status mappings
 */
export const PLAN_ERROR_STATUS: Record<PlanErrorCode, number> = {
  [PlanErrorCode.PLAN_FORBIDDEN_FORMAT]: 403,
  [PlanErrorCode.PLAN_FORBIDDEN_SHARING_MODE]: 403,
  [PlanErrorCode.PLAN_SHAPE_LIMIT_EXCEEDED]: 403,
  [PlanErrorCode.PLAN_UPGRADE_REQUIRED]: 403,
  [PlanErrorCode.ENTITLEMENTS_NOT_RESOLVED]: 500,
  [PlanErrorCode.ENTITLEMENTS_ERROR]: 500,
  [PlanErrorCode.MISSING_CAPABILITY]: 400,
  [PlanErrorCode.MISSING_FORMAT]: 400,
  [PlanErrorCode.CAPABILITY_CHECK_ERROR]: 500,
  [PlanErrorCode.EXPORT_FORMAT_CHECK_ERROR]: 500,
  [PlanErrorCode.SHAPE_LIMIT_ERROR]: 500
};

/**
 * Check if an error code is a plan enforcement error
 */
export function isPlanError(code: string): code is PlanErrorCode {
  return Object.values(PlanErrorCode).includes(code as PlanErrorCode);
}

/**
 * Get the HTTP status code for a plan error
 */
export function getPlanErrorStatus(code: PlanErrorCode): number {
  return PLAN_ERROR_STATUS[code];
}

/**
 * Get the user-friendly description for a plan error
 */
export function getPlanErrorDescription(code: PlanErrorCode): string {
  return PLAN_ERROR_DESCRIPTIONS[code];
}
